import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function DocsRestAPI() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const endpoints = [
    {
      method: 'POST',
      path: '/api/collect/product-view',
      description: 'Track a product page view. Use this for server-side tracking.',
      auth: 'X-CI-Api-Key header (optional)',
      requestBody: `{
  "brand_id": "brand_xyz",
  "product": {
    "product_id": "prod_123",
    "sku": "SKU-123",
    "title": "Product Name",
    "url": "https://brand.com/products/..."
  },
  "context": {
    "timestamp": "2025-12-22T15:30:00Z",
    "session_id": "sess_abc123",
    "country": "US",
    "locale": "en-US"
  }
}`,
      responseBody: `{
  "status": "ok",
  "event_id": "evt_view_abc123def",
  "received_at": "2025-12-22T15:30:01Z"
}`
    },
    {
      method: 'POST',
      path: '/api/collect/product-purchase',
      description: 'Track a completed purchase. Use this for server-side tracking.',
      auth: 'X-CI-Api-Key header (optional)',
      requestBody: `{
  "brand_id": "brand_xyz",
  "order": {
    "order_id": "order_789",
    "currency": "USD",
    "total_value": 129.97,
    "items": [
      {
        "product_id": "prod_123",
        "sku": "SKU-123",
        "title": "Product Name",
        "quantity": 2,
        "unit_price": 39.99
      }
    ]
  },
  "context": {
    "timestamp": "2025-12-22T15:31:00Z",
    "session_id": "sess_abc123"
  }
}`,
      responseBody: `{
  "status": "ok",
  "event_id": "evt_purchase_xyz789",
  "received_at": "2025-12-22T15:31:01Z"
}`
    },
    {
      method: 'GET',
      path: '/api/ci/product-proof',
      description: 'Retrieve social proof data for a specific product.',
      auth: 'X-CI-Api-Key header (optional)',
      params: [
        { name: 'brand_id', type: 'string', required: true, desc: 'Your brand identifier' },
        { name: 'product_id', type: 'string', required: true, desc: 'The product identifier' },
        { name: 'window_views', type: 'integer', required: false, desc: 'Minutes for view count (default: 60)' },
        { name: 'window_purchases', type: 'integer', required: false, desc: 'Hours for purchase count (default: 24)' }
      ],
      requestExample: 'GET /api/ci/product-proof?brand_id=brand_xyz&product_id=prod_123&window_views=60&window_purchases=24',
      responseBody: `{
  "brand_id": "brand_xyz",
  "product_id": "prod_123",
  "views_last_60_min": 37,
  "purchases_last_24h": 12,
  "generated_at": "2025-12-22T15:30:00Z"
}`
    }
  ];

  const errorCodes = [
    { code: '200', desc: 'Success - event accepted or data returned' },
    { code: '400', desc: 'Bad Request - validation error in request body' },
    { code: '401', desc: 'Unauthorized - invalid or missing API key' },
    { code: '404', desc: 'Not Found - brand or product not found' },
    { code: '500', desc: 'Server Error - internal error, retry later' }
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30 mb-4">
          Integration
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          REST API
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Server-side API endpoints for Commerce Index integration.
        </p>
      </div>

      {/* Base URL */}
      <section className="mb-8">
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="pt-6">
            <h3 className="text-white font-medium mb-3">Base URLs</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30">Production</Badge>
                <code className="text-[#E2E8F0]">https://commerceindex.com/api</code>
              </div>
              <div className="flex items-center gap-3">
                <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30">Staging</Badge>
                <code className="text-[#E2E8F0]">https://api.staging.commerceindex.com</code>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Authentication */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Authentication</h2>
        <p className="text-[#94A3B8] mb-4">
          Include your API key in the request headers:
        </p>
        <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
          <code>{`X-CI-Api-Key: your_api_key_here
Content-Type: application/json`}</code>
        </pre>
      </section>

      {/* Endpoints */}
      <section className="space-y-8">
        <h2 className="text-xl font-semibold text-white mb-4">Endpoints</h2>
        
        {endpoints.map((endpoint, index) => (
          <Card key={index} className="bg-[#0D1628] border-[#22304A]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Badge className={endpoint.method === 'GET' 
                  ? 'bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30'
                  : 'bg-[#7A5CFF]/20 text-[#7A5CFF] border-[#7A5CFF]/30'
                }>
                  {endpoint.method}
                </Badge>
                <CardTitle className="text-white font-mono text-lg">
                  {endpoint.path}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[#94A3B8]">{endpoint.description}</p>
              <p className="text-sm text-[#64748B]"><strong>Auth:</strong> {endpoint.auth}</p>
              
              {endpoint.params && (
                <div>
                  <h4 className="text-white font-medium mb-2">Query Parameters</h4>
                  <div className="space-y-2">
                    {endpoint.params.map((param, i) => (
                      <div key={i} className="flex items-start gap-3 py-2 border-b border-[#22304A] last:border-0">
                        <code className="text-[#3EF1C7] text-sm bg-[#3EF1C7]/10 px-2 py-0.5 rounded flex-shrink-0">
                          {param.name}
                        </code>
                        <span className="text-[#7A5CFF] text-sm">{param.type}</span>
                        {param.required && (
                          <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 text-xs">
                            Required
                          </Badge>
                        )}
                        <span className="text-[#94A3B8] text-sm">{param.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <Tabs defaultValue="request" className="w-full">
                <TabsList className="bg-[#1E293B] border border-[#334155]">
                  {endpoint.requestBody && (
                    <TabsTrigger value="request" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                      Request
                    </TabsTrigger>
                  )}
                  {endpoint.requestExample && (
                    <TabsTrigger value="request" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                      Request
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="response" className="data-[state=active]:bg-[#3EF1C7] data-[state=active]:text-[#0B1120]">
                    Response
                  </TabsTrigger>
                </TabsList>
                
                {(endpoint.requestBody || endpoint.requestExample) && (
                  <TabsContent value="request">
                    <div className="relative">
                      <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                        <code>{endpoint.requestBody || endpoint.requestExample}</code>
                      </pre>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                        onClick={() => copyToClipboard(endpoint.requestBody || endpoint.requestExample, `req-${index}`)}
                      >
                        {copiedCode === `req-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </TabsContent>
                )}
                
                <TabsContent value="response">
                  <div className="relative">
                    <pre className="bg-[#0B1120] border border-[#334155] rounded-lg p-4 overflow-x-auto text-sm text-[#E2E8F0]">
                      <code>{endpoint.responseBody}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2 text-[#64748B] hover:text-white"
                      onClick={() => copyToClipboard(endpoint.responseBody, `res-${index}`)}
                    >
                      {copiedCode === `res-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* Error Codes */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-white mb-4">Error Codes</h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="pt-6">
            <div className="space-y-2">
              {errorCodes.map((error, i) => (
                <div key={i} className="flex items-center gap-4 py-2 border-b border-[#22304A] last:border-0">
                  <code className={`text-sm px-2 py-0.5 rounded ${
                    error.code === '200' ? 'bg-[#3EF1C7]/10 text-[#3EF1C7]' :
                    error.code.startsWith('4') ? 'bg-[#FFD166]/10 text-[#FFD166]' :
                    'bg-red-500/10 text-red-400'
                  }`}>
                    {error.code}
                  </code>
                  <span className="text-[#94A3B8] text-sm">{error.desc}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
